import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const MAX_QUANTITY = 99;

/**
 * Splits an order into parcels the way the courier packs them: full parcels
 * first, then whatever is left over (10 pieces, 4 per parcel → [4, 4, 2]).
 * @param {number} quantity
 * @param {number} perParcel
 * @returns {number[]}
 */
function splitIntoParcels(quantity, perParcel) {
  const parcels = Array(Math.floor(quantity / perParcel)).fill(perParcel);
  const remainder = quantity % perParcel;
  if (remainder > 0) parcels.push(remainder);
  return parcels;
}

/**
 * "4 + 4 + 2" for a few parcels; "24 × 4 + 3" once the list would get long.
 * @param {number[]} parcels
 * @param {number} perParcel
 * @returns {string}
 */
function describeParcels(parcels, perParcel) {
  const full = parcels.filter((size) => size === perParcel).length;
  if (full <= 3) return parcels.join(' + ');

  const remainder = parcels.length > full ? parcels[parcels.length - 1] : 0;
  return remainder > 0 ? `${full} × ${perParcel} + ${remainder}` : `${full} × ${perParcel}`;
}

/**
 * International shipping calculator inside the shipping dialog
 * (blocks/kp-shipping-info.liquid).
 *
 * Reads the country list from the JSON rendered by
 * snippets/kp-shipping-rates.liquid, fills the "Shipping to" dropdown and
 * the per-parcel rate table, and estimates the total for any number of pieces.
 * The table lists only the first `data-rates-shown` rates; the estimate uses
 * every rate.
 * Countries without rates are skipped; with none left, only the empty message
 * shows.
 *
 * @typedef {{ code: string, name: string, delivery: string, rates: number[] }} ShippingCountry
 */
class KpShippingCalculator extends HTMLElement {
  /** @type {ShippingCountry[]} */
  countries = [];

  /** @type {AbortController | null} */
  abortController = null;

  connectedCallback() {
    this.countries = this.#readCountries();

    const layout = /** @type {HTMLElement | null} */ (this.querySelector('[data-layout]'));
    const empty = /** @type {HTMLElement | null} */ (this.querySelector('[data-empty]'));
    this.select = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-country-select]'));
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-quantity-input]'));

    if (!layout || !empty || !this.select || !this.input) return;

    layout.hidden = this.countries.length === 0;
    empty.hidden = this.countries.length > 0;
    if (this.countries.length === 0) return;

    this.#renderOptions();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.select.addEventListener('change', () => this.#update(), { signal });
    this.input.addEventListener('input', () => this.#update(), { signal });
    this.input.addEventListener('change', () => this.#setQuantity(this.#readQuantity()), { signal });
    this.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const step = Number(/** @type {HTMLElement} */ (button).dataset.step);
          this.#setQuantity(this.#readQuantity() + step);
        },
        { signal }
      );
    });

    this.#update();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  /** @returns {ShippingCountry[]} */
  #readCountries() {
    const script = this.querySelector('script[data-shipping-rates]');

    try {
      const data = JSON.parse(script?.textContent || '[]');
      if (!Array.isArray(data)) return [];

      return data.filter(
        (country) =>
          country &&
          typeof country.name === 'string' &&
          Array.isArray(country.rates) &&
          country.rates.length > 0 &&
          country.rates.every((rate) => Number.isFinite(rate) && rate >= 0)
      );
    } catch (error) {
      console.error('kp-shipping-calculator: invalid JSON in kp-shipping-rates.liquid', error);
      return [];
    }
  }

  #renderOptions() {
    const select = /** @type {HTMLSelectElement} */ (this.select);
    const shopperCountry = (this.dataset.country || '').toUpperCase();
    const preselected = Math.max(
      0,
      this.countries.findIndex((country) => country.code?.toUpperCase() === shopperCountry)
    );

    select.replaceChildren(
      ...this.countries.map((country, index) => {
        const option = new Option(country.name, String(index));
        option.selected = index === preselected;
        return option;
      })
    );
  }

  /** @returns {ShippingCountry} */
  get #country() {
    return this.countries[Number(this.select?.value) || 0] ?? this.countries[0];
  }

  /** @returns {number} */
  #readQuantity() {
    const value = Math.floor(Number(this.input?.value));
    return Number.isFinite(value) && value >= 1 ? Math.min(value, MAX_QUANTITY) : 1;
  }

  /** @param {number} quantity */
  #setQuantity(quantity) {
    const input = /** @type {HTMLInputElement} */ (this.input);
    input.value = String(Math.min(Math.max(quantity, 1), MAX_QUANTITY));
    this.#update();
  }

  #update() {
    const country = this.#country;
    const perParcel = country.rates.length;
    const shown = Math.min(Number(this.dataset.ratesShown) || perParcel, perParcel);
    const quantity = this.#readQuantity();
    const parcels = splitIntoParcels(quantity, perParcel);
    const total = parcels.reduce((sum, size) => sum + country.rates[size - 1], 0);

    this.#setText('[data-delivery]', country.delivery || '');
    this.#setText('[data-estimate-heading-text]', this.#template('estimateHeading', { max: shown }));
    this.#setText('[data-total]', this.#format(total));
    this.#setText(
      '[data-parcels]',
      parcels.length === 1
        ? this.#template('parcelsOne')
        : this.#template('parcelsMany', { parcels: parcels.length, breakdown: describeParcels(parcels, perParcel) })
    );

    this.#renderRates(country, shown);

    const [minus, plus] = this.querySelectorAll('[data-step]');
    if (minus) /** @type {HTMLButtonElement} */ (minus).disabled = quantity <= 1;
    if (plus) /** @type {HTMLButtonElement} */ (plus).disabled = quantity >= MAX_QUANTITY;
  }

  /**
   * @param {ShippingCountry} country
   * @param {number} shown - rows to list; the "max per parcel" note only
   *   appears when the list reaches the last rate
   */
  #renderRates(country, shown) {
    const tbody = this.querySelector('[data-rate-rows]');
    if (!tbody) return;

    const maxLabel = this.dataset.maxLabel || '';

    tbody.replaceChildren(
      ...country.rates.slice(0, shown).map((rate, index) => {
        const row = document.createElement('tr');
        const size = document.createElement('th');
        const charge = document.createElement('td');
        const isLast = index === country.rates.length - 1;

        size.scope = 'row';
        size.textContent = String(index + 1);
        if (isLast && maxLabel) {
          const note = document.createElement('span');
          note.className = 'kp-shipping-calc__table-note';
          note.textContent = ` ${maxLabel}`;
          size.append(note);
        }

        charge.textContent = this.#format(rate);
        row.append(size, charge);
        return row;
      })
    );
  }

  /**
   * @param {string} selector
   * @param {string} text
   */
  #setText(selector, text) {
    const element = this.querySelector(selector);
    if (element && element.textContent !== text) element.textContent = text;
  }

  /**
   * Fills a translated string rendered into a data attribute, e.g.
   * "Ships as [parcels] parcels: [breakdown] pieces".
   * @param {string} key - dataset key
   * @param {Record<string, string | number>} [values]
   * @returns {string}
   */
  #template(key, values = {}) {
    return Object.entries(values).reduce(
      (text, [name, value]) => text.replaceAll(`[${name}]`, String(value)),
      this.dataset[key] || ''
    );
  }

  /**
   * Formats a rate for the shopper. Rates in kp-shipping-rates.liquid are whole
   * units of the store currency (`data-currency`, INR); the shopper may be
   * browsing in another market currency (`data-active-currency`, e.g. USD).
   * @param {number} amount - Whole store-currency units, e.g. 2500 = ₹2,500
   * @returns {string}
   */
  #format(amount) {
    const storeCurrency = this.dataset.currency || 'INR';
    const activeCurrency = this.dataset.activeCurrency || storeCurrency;
    // Shopify.currency.rate is the live store → active multiplier, as a string.
    const rate = Number(window.Shopify?.currency?.rate);

    if (activeCurrency === storeCurrency || !Number.isFinite(rate) || rate <= 0) {
      return this.#formatInStoreCurrency(amount);
    }

    // Round up so the estimate never undercuts what checkout charges.
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.ceil(amount * rate));
  }

  /**
   * Formats whole store-currency units with the shop's money format, without decimals.
   * @param {number} amount
   * @returns {string}
   */
  #formatInStoreCurrency(amount) {
    const currency = this.dataset.currency || 'INR';
    const unit = convertMoneyToMinorUnits('1', currency) ?? 100;
    const format = (this.dataset.moneyFormat || '{{amount}}')
      .replace(/{{\s*amount_with_comma_separator\s*}}/, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}');

    return formatMoney(amount * unit, format, currency);
  }
}

if (!customElements.get('kp-shipping-calculator')) {
  customElements.define('kp-shipping-calculator', KpShippingCalculator);
}
